import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {
  REACTION_CHEM_SEARCH_DESCRIPTION,
  REACTION_CHEM_SEARCH_MODAL_TITLE,
} from 'chemistry/ChemistryConstants';
import ChemSearchModalFrame from 'chemistry/ChemSearchModalFrame';
import {
  ChemSearchMoleculeOptions,
  ChemSearchTypeToggle,
  ReactionRoleSelector,
} from 'chemistry/ChemSearchModalOptions';
import {chemSearchModalStyles} from 'chemistry/ChemSearchModalStyles';
import {buildReactionChemSearchSubmitPayload, ChemSearchSubmit} from 'chemistry/chemSearchSubmit';
import {
  getDefaultChemSearchOperator,
  getDefaultMinSimilarity,
  ToggleHighlightParams,
} from 'chemistry/ChemSearchUtils';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {getDefaultReactionComponentRole} from 'chemistry/ReactionChemSearchUtils';
import {useChemSearchModalSubmit} from 'chemistry/useChemSearchModalSubmit';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {ChemicalStructureSearchOperator, SearchParams} from 'search/SearchConstants';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import {GRAY_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';

export interface ReactionChemSearchModalProps {
  initialMol: string | null;
  onRequestSubmit: (submit: ChemSearchSubmit | null) => void;
  onRequestClose: () => void;
  toggleHighlightParams: ToggleHighlightParams;
  searchParams: SearchParams;
}

export default function ReactionChemSearchModal({
  initialMol,
  onRequestSubmit,
  onRequestClose,
  toggleHighlightParams,
  searchParams,
}: ReactionChemSearchModalProps): JSX.Element {
  const [mol, setMol] = useState<ChemicalStructureDrawerValue>(initialMol);
  const [highlightChecked, setHighlightChecked] = useState<boolean>(
    toggleHighlightParams.shouldHighlightSubstructure
  );
  const [chemSearchOperator, setChemSearchOperator] = useState<ChemicalStructureSearchOperator>(
    getDefaultChemSearchOperator(searchParams)
  );
  const [minSimilarity, setMinSimilarity] = useState<number>(getDefaultMinSimilarity());
  const [reactionComponentRole, setReactionComponentRole] = useState<ChemistryReactionRole | null>(() =>
    getDefaultReactionComponentRole(searchParams)
  );

  const buildSubmitResult = useCallback(
    () =>
      buildReactionChemSearchSubmitPayload({
        mol,
        chemSearchOperator,
        minSimilarity,
        reactionComponentRole,
      }),
    [chemSearchOperator, minSimilarity, mol, reactionComponentRole]
  );

  const {handleSubmit} = useChemSearchModalSubmit({
    toggleHighlightParams,
    highlightChecked,
    chemSearchOperator,
    onRequestSubmit,
    buildSubmitResult,
  });

  return (
    <ChemSearchModalFrame
      showHeaderBorder={false}
      title={REACTION_CHEM_SEARCH_MODAL_TITLE}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <div className={css(styles.reactionSearchIntro)}>
        <p className={css(styles.reactionSearchDescription)}>{REACTION_CHEM_SEARCH_DESCRIPTION}</p>
      </div>
      <div className={css(chemSearchModalStyles.chemicalDrawer)}>
        <ChemicalStructureDrawer
          onMoleculeChange={setMol}
          initialValue={mol}
          mode={ChemicalStructureDrawerMode.STANDARD}
        />
      </div>
      <div className={css(chemSearchModalStyles.searchOptionsContainer)}>
        <div className={css(chemSearchModalStyles.searchOptions)}>
          <ChemSearchTypeToggle
            chemSearchOperator={chemSearchOperator}
            setChemSearchOperator={setChemSearchOperator}
          />
          <ReactionRoleSelector
            reactionComponentRole={reactionComponentRole}
            setReactionComponentRole={setReactionComponentRole}
          />
          <ChemSearchMoleculeOptions
            highlightChecked={highlightChecked}
            setHighlightChecked={setHighlightChecked}
            chemSearchOperator={chemSearchOperator}
            minSimilarity={minSimilarity}
            setMinSimilarity={setMinSimilarity}
            toggleHighlightParams={toggleHighlightParams}
          />
        </div>
      </div>
    </ChemSearchModalFrame>
  );
}

const styles = StyleSheet.create({
  reactionSearchIntro: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: '0 16px 16px',
  },
  reactionSearchDescription: {
    ...FONT_PROXIMA_12,
    color: GRAY_SEMIDARK,
    margin: 0,
  },
});
